// ── Programme catalogue types ───────────────────────────────
// Miroir exact du schéma JSON des fichiers programs/*.json

export interface Exercise {
  order: number
  category: string
  name: string
  // ID (ou "id1 + id2" pour un combiné) résolu vers `exercises.id` par
  // scripts/migrate-exercise-ids.js. Absent = pas encore migré → fallback
  // normalizeExerciseName() côté runtime.
  exercise_id?: string
  muscle_main: string
  muscle_secondary: string
  // `sets` et `reps` sont tantôt écrits en nombre ("sets": 3, "reps": 10), tantôt
  // en texte ("reps": "30''", "12/côté", "8+4+3"). Le catalogue en compte 5 955 et
  // 1 558 en nombre — les deux formes sont dans les données, le type doit le dire.
  // Le déclarer `string` a coûté un écran de séance en erreur : `mapExercise`
  // recopiait `reps` tel quel, et `reps.trim()` échouait à l'affichage.
  // `mapExercise` les normalise en texte une fois pour toutes ; personne d'autre
  // ne doit lire ces deux champs bruts.
  sets: number | string
  reps: number | string
  tempo: string
  load: string
  rest_between_sets: string
  rest_type: string
  rest_between_exercises: string | null
  notes: string | null
  female_note: string | null
  home_substitute: string | null
  /**
   * Séances de test uniquement : les trois montées du protocole, dans l'ordre. Source de
   * vérité du contenu — ajuster un protocole se fait ici, pas dans le code, qui ne fournit
   * qu'un repli par niveau. Voir `parseTestProtocol` pour la validation.
   */
  test_protocol?: Array<{
    set: number
    reps: number
    percent_1rm: number
    rest_sec: number
    max_effort?: boolean
  }>
  /**
   * Méthodes composées uniquement (rest-pause, bi-set, drop set, pyramide montante,
   * cluster, myo-reps) : les paliers de la série, dans l'ordre d'exécution. Absent sur
   * une série droite — c'est cette absence qui dit « pas de schéma de série ».
   *
   * Source de vérité du contenu, comme `test_protocol` : ajuster un protocole se fait
   * ici, pas dans le code. `reps` et `load` parlent le même vocabulaire que les champs
   * du même nom sur l'exercice — `load` est résolu par `computeChargeKg`, une seule
   * grammaire de charge dans tout le système.
   *
   * Écrit une fois par `scripts/migrate-set-schemes.js` depuis `reps`/`load`/`notes`,
   * puis relu à la main. Validé au chargement par `parseSetScheme`, qui renvoie null
   * sur tout contenu douteux plutôt que d'afficher un schéma faux.
   */
  set_scheme?: {
    method: "rest-pause" | "drop-set" | "pyramide-montante" | "bi-set" | "cluster" | "myo-reps"
    label: string
    paliers: Array<{
      reps: string
      load: string
      /** Renseigné seulement si le palier change de mouvement (bi-set). */
      movement?: string
      /** Absent sur le dernier palier. */
      transition?:
        | { kind: "pause"; seconds: number; estimated?: boolean }
        | { kind: "load-change" }
        | { kind: "chain" }
    }>
  }
}

/**
 * Item d'échauffement.
 *
 * Historiquement une simple chaîne ("Pull Up 2x10"), d'où l'absence d'`exercise_id` :
 * il n'y avait aucun champ où le loger, et l'échauffement était donc exclu de
 * `scripts/migrate-exercise-ids.js`. La forme objet, écrite par ce script, porte le nom
 * et les reps déjà découpés (`parseWarmupLabel`) plus l'id résolu.
 *
 * Les DEUX formes se lisent : les lignes `program_sessions` restent en chaînes tant
 * qu'elles n'ont pas été re-seedées, et une chaîne reste une donnée valide ensuite.
 */
export type WarmupItem =
  | string
  | { name: string; reps?: string; exercise_id?: string }

export interface Warmup {
  mobilite: WarmupItem[]
  cardio: WarmupItem
  activation: WarmupItem[]
}

export interface ProgramSession {
  id?: number
  programme_id: string
  session_number: number
  week: number
  cycle: number
  type: string
  label: string
  warmup: Warmup
  format_travail: string | null
  cooldown: string | null
  exercises: Exercise[]
}

export interface ProgramCycle {
  cycle: number
  weeks: string
  method: string
  load_progression: string
}

export interface Program {
  programme_id: string
  level: string
  goal: string
  environment: string
  gender: string
  duration_weeks: number
  sessions_total: number
  structure: string | null
  cycles: ProgramCycle[]
  created_at?: string
  updated_at?: string
}

// ── Label maps (French label → wizard key) ───────────────────
export const LEVEL_MAP: Record<string, string> = {
  beginner:     "Débutant",
  intermediate: "Intermédiaire",
  advanced:     "Avancé",
}

export const GOAL_MAP: Record<string, string> = {
  "perte-de-poids":        "Perte de poids",
  "prise-de-muscle":       "Prise de muscle",
  "remise-en-forme":       "Remise en forme & Tonification",
  "seche-definition":      "Sèche & Définition",
  "construction-musculaire": "Construction Musculaire",
  "force-endurance":       "Force & Endurance",
  "decoupe-extreme":       "Découpe Extrême",
  "hypertrophie":          "Hypertrophie",
  "force-athletique":      "Force Athlétique",
}

export const PLACE_MAP: Record<string, string> = {
  gym:  "Salle",
  home: "Maison",
}

export const GENDER_MAP: Record<string, string> = {
  male:   "Homme",
  female: "Femme",
}

export function buildProgrammeId(
  levelId: string,
  goalId: string,
  place: string,
  gender: string
): string {
  return `${levelId}_${goalId}_${place}_${gender}`
}

type ProgrammeIdentity = { level_id: string; goal_id: string; place: string; gender: string }

/**
 * Champ à ajouter à une mise à jour de `wizard_submissions` qui change le programme À LA
 * MAIN (Réglages, profil, diagnostic refait) : `program_start_session` revient à 1, le
 * nouveau programme se commence par sa semaine 1. Sans cette remise à zéro, un client
 * passé à 5 par un changement de niveau commencerait aussi à 5 un programme choisi
 * ensuite, test initial compris sauté. Seul `check-and-populate-next-block` écrit 5.
 *
 * Renvoie `{}` si la mise à jour ne change pas le programme.
 */
export function programStartResetFor(
  current: ProgrammeIdentity,
  updates: Partial<ProgrammeIdentity>
): { program_start_session?: number } {
  const before = buildProgrammeId(current.level_id, current.goal_id, current.place, current.gender)
  const after = buildProgrammeId(
    updates.level_id ?? current.level_id,
    updates.goal_id ?? current.goal_id,
    updates.place ?? current.place,
    updates.gender ?? current.gender
  )
  return after !== before ? { program_start_session: 1 } : {}
}
